import React, { useState, useEffect } from 'react';
import { useVCOnboarding } from '../../../contexts/VCOnboardingContext';
import {
  VCPreferences,
  UpdateFrequency,
  UPDATE_FREQUENCY_LABELS,
  UPDATE_FREQUENCY_DESCRIPTIONS,
} from '../../../types/vcOnboarding';
import styles from '../VCOnboarding.module.css';

const Step5Preferences: React.FC = () => {
  const { data, saveStep, previousStep, completeOnboarding, loading } = useVCOnboarding();
  
  const [formData, setFormData] = useState<VCPreferences>({
    updateFrequency: 'monthly',
  });
  
  const [errors, setErrors] = useState<Partial<Record<keyof VCPreferences, string>>>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (data.step5_preferences) {
      setFormData(data.step5_preferences);
    }
  }, [data.step5_preferences]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setIsSaving(true);
      // Save the final step
      await saveStep(5, formData);
      // Mark onboarding as complete
      await completeOnboarding();
    } catch (error) {
      console.error('Error completing onboarding:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const selectFrequency = (frequency: UpdateFrequency) => {
    setFormData({ updateFrequency: frequency });
  };

  const frequencies: UpdateFrequency[] = ['weekly', 'bi_weekly', 'monthly', 'quarterly'];

  return (
    <form onSubmit={handleSubmit}>
      <div className={styles.card}>
        <h2 className={styles.cardTitle}>Update preferences</h2>
        <p className={styles.cardDescription}>
          How often would you like to receive portfolio updates?
        </p>

        <div className={styles.radioGroup}>
          {frequencies.map((frequency) => (
            <button
              key={frequency}
              type="button"
              className={`${styles.radioOption} ${
                formData.updateFrequency === frequency ? styles.selected : ''
              }`}
              onClick={() => selectFrequency(frequency)}
              disabled={isSaving || loading}
            >
              <div className={styles.radioContent}>
                <div className={styles.radioTitle}>
                  {UPDATE_FREQUENCY_LABELS[frequency]}
                </div>
                <div className={styles.radioDescription}>
                  {UPDATE_FREQUENCY_DESCRIPTIONS[frequency]}
                </div>
              </div>
              <div className={styles.radioCheckbox}>
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </button>
          ))}
        </div>

        <div className={styles.completionBanner}>
          <div className={styles.completionIcon}>⚡</div>
          <div className={styles.completionText}>
            <div className={styles.completionTitle}>Ready to invite your portfolio!</div>
            <div className={styles.completionDescription}>
              After setup, you&apos;ll be able to invite portfolio companies to
              connect their data sources and start sending automated updates.
            </div>
          </div>
        </div>

        <div className={styles.buttonGroup}>
          <button
            type="button"
            className={`${styles.button} ${styles.buttonSecondary}`}
            onClick={previousStep}
            disabled={isSaving || loading}
          >
            <svg className={styles.buttonIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <button
            type="submit"
            className={`${styles.button} ${styles.buttonPrimary}`}
            disabled={isSaving || loading}
          >
            {isSaving ? (
              <>
                <span className={styles.loadingSpinner} />
                Completing...
              </>
            ) : (
              <>
                Complete Setup
                <svg className={styles.buttonIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </>
            )}
          </button>
        </div>
      </div>
    </form>
  );
};

export default Step5Preferences;

